import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  SelectPanelScrollBarProps,
  SelectPanelScrollBarViewModel,
} from "./types-select-panel-scroll-bar";

/** Põe a faixa da barra sobre a área que rola (o painel é o `offsetParent`); a medida vai por ref, sem estado. */
function fitOverlay(overlay: HTMLElement | null, scroller: HTMLElement | null) {
  if (!overlay || !scroller) return;
  overlay.style.top = `${scroller.offsetTop}px`;
  overlay.style.height = `${scroller.offsetHeight}px`;
}

/**
 * O painel do Prime é portal e a barra nasce no rodapé dele (`panelFooterTemplate`): daqui o rodapé é filho do painel,
 * irmão da área que rola (`wrapperSelector`), que é o alvo da `ScrollBar`.
 */
export function useSelectPanelScrollBar({
  wrapperSelector,
}: SelectPanelScrollBarProps): SelectPanelScrollBarViewModel {
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const overlay = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useMemo(() => ({ current: scroller }), [scroller]);

  const overlayRef = useCallback(
    (element: HTMLDivElement | null) => {
      overlay.current = element;
      const panel = element?.parentElement?.parentElement;
      setScroller(panel?.querySelector<HTMLElement>(wrapperSelector) ?? null);
    },
    [wrapperSelector],
  );

  useEffect(() => {
    if (!scroller) return;
    fitOverlay(overlay.current, scroller);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => fitOverlay(overlay.current, scroller));
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [scroller]);

  return { overlayRef, scrollerRef, hasScroller: scroller !== null };
}
